/**
 * useWeatherForecasts — fetch NWS forecasts for a list of truck events
 * in one batch round-trip.
 *
 * Backend gates visibility via two feature flags:
 *   - enable_weather_chip (master)
 *   - weather_chip_admin_only (restricts to admins until flipped OFF)
 * If neither is true for this caller, the response is `{enabled: false}`
 * and this hook returns `{ enabled: false, byId: new Map(), header: null }`
 * — the UI simply renders nothing and there's nothing to leak.
 *
 * Strategy:
 *  - Build a stable cache key from sorted `truck_id|starts_at` pairs.
 *  - Refetch only when that key changes — so panning the map doesn't
 *    re-request, but switching the date window does.
 *  - We also compute a "header forecast" for *now* at the centroid of
 *    the visible trucks (used by the "Today across the county" chip).
 *  - Hard-cap the batch at 50 entries to match the backend limit.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "./api";

const MAX_BATCH = 200;

function _avgCoord(values) {
  if (!values.length) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

export default function useWeatherForecasts(trucks) {
  const [state, setState] = useState({
    enabled: false,
    byId: new Map(),
    header: null,
  });
  const inFlightRef = useRef(null);

  // Stable cache key. We only refetch when the set of (truck, event-time)
  // pairs meaningfully changes — pan/zoom alone shouldn't trigger.
  const cacheKey = useMemo(() => {
    if (!Array.isArray(trucks) || trucks.length === 0) return "";
    const slice = trucks
      .slice(0, MAX_BATCH)
      .map(
        (t) =>
          `${t.truck_id || t.id}|${t.starts_at || ""}|${t.lat ?? ""},${t.lng ?? ""}`
      )
      .sort()
      .join(";");
    return slice;
  }, [trucks]);

  useEffect(() => {
    if (!cacheKey) {
      setState({ enabled: false, byId: new Map(), header: null });
      return;
    }
    // Don't double-fetch the same key (StrictMode / re-render bursts).
    if (inFlightRef.current === cacheKey) return;
    inFlightRef.current = cacheKey;

    const sample = trucks.slice(0, MAX_BATCH);
    const points = sample
      .filter(
        (t) =>
          typeof t.lat === "number" &&
          typeof t.lng === "number" &&
          !!t.starts_at
      )
      .map((t) => ({
        lat: t.lat,
        lng: t.lng,
        when: t.starts_at,
      }));

    // Header chip lookup — forecast for the centroid + a `when` that
    // reflects which day the user is viewing:
    //   - "now" if any visible truck is currently live (Today filter
    //     with active trucks → user wants real-time weather)
    //   - else the EARLIEST upcoming truck's start time (Week filter
    //     or future-day → "weather at the next thing to happen")
    //   - else "now" as a last-resort fallback.
    // This is what makes the header chip respond when the user
    // toggles between Today and Week filters.
    const lats = sample.map((t) => t.lat).filter((v) => typeof v === "number");
    const lngs = sample.map((t) => t.lng).filter((v) => typeof v === "number");
    const centroidLat = _avgCoord(lats);
    const centroidLng = _avgCoord(lngs);
    const nowMs = Date.now();
    let headerWhenIso = new Date(nowMs).toISOString();
    let earliestUpcomingMs = Infinity;
    let anyLive = false;
    for (const t of sample) {
      if (!t.starts_at || !t.ends_at) continue;
      const s = Date.parse(t.starts_at);
      const e = Date.parse(t.ends_at);
      if (Number.isFinite(s) && Number.isFinite(e) && s <= nowMs && nowMs <= e) {
        anyLive = true;
        break;
      }
      if (Number.isFinite(s) && s > nowMs && s < earliestUpcomingMs) {
        earliestUpcomingMs = s;
      }
    }
    if (!anyLive && Number.isFinite(earliestUpcomingMs)) {
      headerWhenIso = new Date(earliestUpcomingMs).toISOString();
    }
    const headerPoint =
      centroidLat != null && centroidLng != null
        ? {
            lat: centroidLat,
            lng: centroidLng,
            when: headerWhenIso,
          }
        : null;

    // Combined batch: header point (if any) + per-truck event points.
    const combinedPoints = headerPoint ? [headerPoint, ...points] : points;
    if (combinedPoints.length === 0) {
      setState({ enabled: false, byId: new Map(), header: null });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/weather/batch", { points: combinedPoints });
        if (cancelled) return;
        if (!data || !data.enabled) {
          setState({ enabled: false, byId: new Map(), header: null });
          return;
        }
        const forecasts = Array.isArray(data.forecasts) ? data.forecasts : [];
        let cursor = 0;
        let header = null;
        if (headerPoint) {
          header = forecasts[cursor] || null;
          cursor += 1;
        }
        const byId = new Map();
        for (const t of sample) {
          if (
            typeof t.lat !== "number" ||
            typeof t.lng !== "number" ||
            !t.starts_at
          ) {
            continue;
          }
          const f = forecasts[cursor];
          cursor += 1;
          if (f) {
            byId.set(t.truck_id || t.id, f);
          }
        }
        setState({ enabled: true, byId, header });
      } catch {
        // Weather is a nice-to-have — failures must never break the map.
        if (!cancelled) {
          setState({ enabled: false, byId: new Map(), header: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return state;
}
